import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import {
  Mail,
  Server,
  Lock,
  User,
  Send,
  Save,
  CheckCircle,
  XCircle,
  Loader2,
  AlertCircle,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const ConfiguracionEmail = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [config, setConfig] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_from: '',
    use_tls: true,
    verify_ssl: true
  });
  const [isConfigured, setIsConfigured] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.get('/system-config/smtp');
      const data = response.data;

      setIsConfigured(data.configured);
      if (data.configured) {
        setConfig({
          smtp_host: data.smtp_host || '',
          smtp_port: data.smtp_port || 587,
          smtp_user: data.smtp_user || '',
          smtp_password: '', // Never returned from API
          smtp_from: data.smtp_from || '',
          use_tls: data.use_tls !== false,
          verify_ssl: data.verify_ssl !== false
        });
      }
    } catch (error) {
      console.error('Error loading SMTP config:', error);
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config.smtp_host || !config.smtp_user || !config.smtp_password) {
      toast.error('Completa todos los campos obligatorios');
      return;
    }

    setSaving(true);
    try {
      await api.post('/system-config/smtp', config);
      toast.success('Configuración guardada correctamente');
      setIsConfigured(true);
    } catch (error) {
      console.error('Error saving SMTP config:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      toast.error('Ingresa un email para la prueba');
      return;
    }

    setTesting(true);
    try {
      await api.post('/system-config/smtp/test', { email: testEmail });
      toast.success(`Email de prueba enviado a ${testEmail}`);
    } catch (error) {
      console.error('Error testing SMTP:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar email de prueba');
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('¿Estás seguro de eliminar la configuración de email?')) {
      return;
    }

    setDeleting(true);
    try {
      await api.delete('/system-config/smtp');
      toast.success('Configuración eliminada');
      setIsConfigured(false);
      setConfig({
        smtp_host: '',
        smtp_port: 587,
        smtp_user: '',
        smtp_password: '',
        smtp_from: '',
        use_tls: true,
        verify_ssl: true
      });
    } catch (error) {
      console.error('Error deleting SMTP config:', error);
      toast.error('Error al eliminar configuración');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Configuración de Email</h1>
          <p className="text-slate-600 mt-2">
            Configura el servidor SMTP para enviar facturas y notificaciones por email
          </p>
        </div>

        {/* Status Card */}
        <div className={`p-4 rounded-xl border ${isConfigured ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex items-center gap-3">
            {isConfigured ? (
              <>
                <CheckCircle className="w-6 h-6 text-green-600" />
                <div>
                  <p className="font-semibold text-green-800">Email Configurado</p>
                  <p className="text-sm text-green-700">El servidor SMTP está listo para enviar emails</p>
                </div>
              </>
            ) : (
              <>
                <AlertCircle className="w-6 h-6 text-amber-600" />
                <div>
                  <p className="font-semibold text-amber-800">Email No Configurado</p>
                  <p className="text-sm text-amber-700">Configura el servidor SMTP para habilitar el envío de emails</p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Configuration Form */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
            <Server className="w-6 h-6 text-slate-600" />
            Configuración SMTP
          </h2>

          <div className="space-y-5">
            {/* Host and Port */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Servidor SMTP *
                </label>
                <input
                  type="text"
                  value={config.smtp_host}
                  onChange={(e) => setConfig({ ...config, smtp_host: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="smtp.gmail.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Puerto *
                </label>
                <input
                  type="number"
                  value={config.smtp_port}
                  onChange={(e) => setConfig({ ...config, smtp_port: parseInt(e.target.value) || 587 })}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="587"
                />
              </div>
            </div>

            {/* User and Password */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <User className="w-4 h-4 inline mr-1" />
                  Usuario SMTP *
                </label>
                <input
                  type="email"
                  value={config.smtp_user}
                  onChange={(e) => setConfig({ ...config, smtp_user: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="tu-email@gmail.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <Lock className="w-4 h-4 inline mr-1" />
                  Contraseña *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={config.smtp_password}
                    onChange={(e) => setConfig({ ...config, smtp_password: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 pr-20"
                    placeholder={isConfigured ? '••••••••' : 'Contraseña o App Password'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500 hover:text-slate-700"
                  >
                    {showPassword ? 'Ocultar' : 'Mostrar'}
                  </button>
                </div>
              </div>
            </div>

            {/* From Email */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <Mail className="w-4 h-4 inline mr-1" />
                Email Remitente (opcional)
              </label>
              <input
                type="email"
                value={config.smtp_from}
                onChange={(e) => setConfig({ ...config, smtp_from: e.target.value })}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="facturas@tuempresa.com (si es diferente al usuario)"
              />
              <p className="text-xs text-slate-500 mt-1">
                Si lo dejas vacío, se usará el email del usuario SMTP
              </p>
            </div>

            {/* TLS Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">Usar TLS/STARTTLS</p>
                <p className="text-sm text-slate-500">Recomendado para Gmail, Outlook, etc.</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.use_tls}
                  onChange={(e) => setConfig({ ...config, use_tls: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
              </label>
            </div>

            {/* Verify SSL Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">Verificar certificado SSL</p>
                <p className="text-sm text-slate-500">Desactiva para servidores con certificados autofirmados (cPanel, hosting propio)</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.verify_ssl}
                  onChange={(e) => setConfig({ ...config, verify_ssl: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
              </label>
            </div>

            {!config.verify_ssl && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <strong>Nota:</strong> La verificación SSL está desactivada. Esto permite conectar con servidores
                  que usan certificados autofirmados (común en cPanel y hostings compartidos), pero reduce la seguridad de la conexión.
                </p>
              </div>
            )}

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Tip para Gmail:</strong> Usa una "Contraseña de Aplicación" en lugar de tu contraseña normal.
                Puedes crearla en tu cuenta de Google &gt; Seguridad &gt; Contraseñas de aplicaciones.
              </p>
            </div>

            {/* Save Button */}
            <div className="flex gap-3">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors"
              >
                {saving ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Save className="w-5 h-5" />
                )}
                {saving ? 'Guardando...' : 'Guardar Configuración'}
              </button>

              {isConfigured && (
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-red-50 text-red-600 font-semibold rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors"
                >
                  {deleting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Trash2 className="w-5 h-5" />
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Test Email Section */}
        {isConfigured && (
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Send className="w-6 h-6 text-slate-600" />
              Probar Configuración
            </h2>
            <p className="text-slate-600 mb-4">
              Envía un email de prueba para verificar que la configuración funciona correctamente.
            </p>

            <div className="flex gap-3">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="email@ejemplo.com"
              />
              <button
                onClick={handleTest}
                disabled={testing || !testEmail}
                className="flex items-center gap-2 px-6 py-2.5 bg-slate-800 text-white font-semibold rounded-lg hover:bg-slate-900 disabled:opacity-50 transition-colors"
              >
                {testing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
                {testing ? 'Enviando...' : 'Enviar Prueba'}
              </button>
            </div>
          </div>
        )}

        {/* Common SMTP Configurations */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-bold text-slate-900 mb-4">Configuraciones Comunes</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => setConfig({ ...config, smtp_host: 'smtp.gmail.com', smtp_port: 587, use_tls: true, verify_ssl: true })}
              className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 text-left transition-colors"
            >
              <p className="font-semibold text-slate-900">Gmail</p>
              <p className="text-sm text-slate-500">smtp.gmail.com:587</p>
            </button>
            <button
              onClick={() => setConfig({ ...config, smtp_host: 'smtp.office365.com', smtp_port: 587, use_tls: true, verify_ssl: true })}
              className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 text-left transition-colors"
            >
              <p className="font-semibold text-slate-900">Outlook / Office 365</p>
              <p className="text-sm text-slate-500">smtp.office365.com:587</p>
            </button>
            <button
              onClick={() => setConfig({ ...config, smtp_host: 'smtp.zoho.com', smtp_port: 587, use_tls: true, verify_ssl: true })}
              className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 text-left transition-colors"
            >
              <p className="font-semibold text-slate-900">Zoho Mail</p>
              <p className="text-sm text-slate-500">smtp.zoho.com:587</p>
            </button>
            <button
              onClick={() => setConfig({ ...config, smtp_host: 'mail.tudominio.com', smtp_port: 465, use_tls: false, verify_ssl: false })}
              className="p-4 border border-amber-200 rounded-lg hover:bg-amber-50 text-left transition-colors"
            >
              <p className="font-semibold text-slate-900">cPanel / Hosting</p>
              <p className="text-sm text-slate-500">mail.tudominio.com:465 (SSL, sin verificar cert)</p>
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ConfiguracionEmail;
